import type { NodeData } from "@/workflow_api/types/workflow";
import type { InteroperabilityConfiguration } from "../types";
import type { NodeVariable } from "../../types";

/**
 * Mapea el tipo del backend al tipo de parámetro del frontend
 */
function mapBackendTypeToParameterType(backendType: string): string {
  switch (backendType.toUpperCase()) {
    case "STRING":
      return "string";
    case "INTEGER":
    case "NUMBER":
    case "FLOAT":
      return "number";
    case "BOOLEAN":
      return "boolean";
    case "DATE":
      return "date";
    case "ARRAY":
      return "array";
    default:
      return "string";
  }
}

/**
 * Helper function to find variable name by ID
 */
function findVariableNameById(
  variableId: string,
  variables?: NodeVariable[],
): string | null {
  if (!variableId || !variables) return null;
  for (const node of variables) {
    const variable = node.variables.find((v) => v.id === variableId);
    if (variable) {
      return variable.name;
    }
  }
  return null;
}

/**
 * Deserializa los inputs del nodo a configuración de interoperabilidad
 */
export function deserializeInteroperabilityNode(
  nodeData: NodeData,
  variables?: NodeVariable[],
): InteroperabilityConfiguration | null {
  const inputs = nodeData.inputs || [];

  // Buscar service input
  const serviceInput = inputs.find((input) => input.name === "service");
  if (!serviceInput || typeof serviceInput.value !== "string") {
    return null;
  }

  // Buscar action input
  const actionInput = inputs.find((input) => input.name === "action");
  if (!actionInput || typeof actionInput.value !== "string") {
    return null;
  }

  // Obtener todos los inputs que son parámetros (excluyendo service y action)
  const parameterInputs = inputs.filter(
    (input) => input.name !== "service" && input.name !== "action",
  );

  // Convertir parámetros a inputMappings
  const inputMappings: Array<{
    id: string;
    parameterId: string;
    variableId: string;
    variableName: string;
    variableType: string;
    isRequired: boolean;
  }> = [];

  parameterInputs.forEach((input) => {
    const parameterId = input.name;

    let variableId = "";
    let variableName = "";

    if (input.global_type && input.value) {
      const nodeId = input.value as string;
      const allInputVariables =
        variables?.flatMap((node) => node.variables) || [];
      const globalVariable = allInputVariables.find(
        (v) => v.node_id === nodeId && v.global_type === input.global_type,
      );

      if (globalVariable) {
        variableId = globalVariable.id;
        variableName = globalVariable.name;
      } else {
        const idSuffix =
          input.global_type === "NODE_EXECUTION_START"
            ? "execution_start"
            : "execution_finish";
        variableId = `${nodeId}_${idSuffix}`;
        variableName =
          input.global_type === "NODE_EXECUTION_START"
            ? "inicio-nodo"
            : "fin-nodo";
      }
    } else {
      const hasSourceConnections =
        input.source_connections && input.source_connections.length > 0;
      const isVariableConnection = hasSourceConnections || !!input.connection;

      if (isVariableConnection) {
        if (hasSourceConnections) {
          const sourceConn = input.source_connections![0];
          if (
            sourceConn.output_name &&
            !sourceConn.field_id &&
            !sourceConn.field
          ) {
            variableId = `${sourceConn.source_node_id}-${sourceConn.output_name}`;
          } else {
            variableId = sourceConn.field_id || sourceConn.field?.id || "";
          }
          const foundName = findVariableNameById(variableId, variables);
          variableName = foundName || sourceConn.field?.name || variableId;
        } else {
          variableId = input.field_id || input.field || "";
          const foundName = findVariableNameById(variableId, variables);
          variableName = foundName || variableId;
        }
      } else {
        if (input.value === null || input.value === undefined) {
          variableName = "";
        } else if (typeof input.value === "number") {
          variableName = String(input.value);
        } else if (typeof input.value === "boolean") {
          variableName = String(input.value);
        } else {
          variableName = String(input.value);
        }
      }
    }

    inputMappings.push({
      id: `input-${parameterId}`,
      parameterId,
      variableId,
      variableName,
      variableType: mapBackendTypeToParameterType(input.type),
      isRequired: false,
    });
  });

  const legacyParamInputs = inputs.filter((input) => input.name === "param");
  legacyParamInputs.forEach((paramInput) => {
    if (paramInput.inputs) {
      const nestedInput = paramInput.inputs;
      const parameterId = nestedInput.name;

      const hasSourceConnections =
        paramInput.source_connections &&
        paramInput.source_connections.length > 0;
      const isVariableConnection =
        hasSourceConnections || !!nestedInput.connection;

      let variableId = "";
      let variableName = "";

      if (isVariableConnection) {
        if (hasSourceConnections) {
          const sourceConn = paramInput.source_connections![0];
          variableId = sourceConn.field_id || sourceConn.field?.id || "";
          const foundName = findVariableNameById(variableId, variables);
          variableName = foundName || sourceConn.field?.name || variableId;
        } else {
          variableId = nestedInput.field_id || nestedInput.field || "";
          const foundName = findVariableNameById(variableId, variables);
          variableName = foundName || variableId;
        }
      } else {
        // Es un valor directo
        if (nestedInput.value === null || nestedInput.value === undefined) {
          variableName = "";
        } else {
          variableName = String(nestedInput.value);
        }
      }

      inputMappings.push({
        id: `input-${parameterId}`,
        parameterId,
        variableId,
        variableName,
        variableType: mapBackendTypeToParameterType(nestedInput.type),
        isRequired: false,
      });
    }
  });

  const result = {
    serviceId: serviceInput.value,
    endpointId: actionInput.value,
    inputMappings,
    outputMappings: [], // Los output mappings no se guardan en inputs
    errorHandling: {
      enabled: false,
      fallbackAction: "continue" as const,
      retryAttempts: 3,
      retryDelay: 1000,
    },
    isServiceValidated: false,
    lastValidationDate: undefined,
  };

  return result;
}
